import { SelectChangeEvent } from '@mui/material/Select/SelectInput';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { Transaction } from 'functions/src/types/firestore/Game/Collection/Item/Transaction';
import { TransactionTableRow } from './TransactionTableRow';
import { useRouter } from 'next/router';
import { Item } from 'functions/src/types/firestore/Game/Collection/Item';
import { Game } from 'functions/src/types/firestore/Game';
import { useEffect, useRef, useState } from 'react';
import {
  getDocs,
  collection,
  query,
  where,
  orderBy,
  collectionGroup,
} from 'firebase/firestore';
import { RentalFulfillment } from 'functions/src/types/firestore/Game/Collection/Item/RentalFulfillment';
import { FulfillmentConverter } from 'src/util/converters/FulfillmentConverter';
import { ConverterFactory } from 'functions/src/util/firestore/ConverterFactory';
import { ELEVATIONS } from 'src/styles/theme';
import { PaginationOptions } from '../PaginationOptions';
import { firestore } from 'src/config/firebaseClient';

type TransactionActivityTabProps = {
  parent: 'item' | 'game';
} & Partial<Pick<Item<Date>, 'contract'>> &
  Partial<Pick<Game<Date>, 'id'>>;

export function TransactionActivityTab({
  id,
  contract,
  parent,
}: TransactionActivityTabProps) {
  const router = useRouter();
  const { asPath } = router;
  const ref = useRef<HTMLElement>(null);
  useEffect(() => {
    if (ref.current && asPath && asPath.split('#')[1] === 'history') {
      ref.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [ref, asPath]);
  const [rowsPerPage, setRowsPerPage] = useState<number>(5);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const handleNextPage = () => {
    setCurrentPage((prev) => {
      return prev + 1;
    });
  };
  const handlePreviousPage = () => {
    setCurrentPage((prev) => {
      return prev - 1;
    });
  };

  const handleRowsPerPage = (e: SelectChangeEvent) => {
    setRowsPerPage(Number(e.target.value));
  };

  const [transactions, setTransactions] = useState<Transaction<Date>[] | []>(
    [],
  );
  //display the transactions for the current page
  const transactionsToDisplay = transactions.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage,
  );
  useEffect(() => {
    const handler = async () => {
      const transactionsRef = collection(firestore, 'Transaction');
      const transactionsQuery =
        parent === 'game'
          ? query(transactionsRef, where('gameId', '==', id))
          : query(
              transactionsRef,
              where('contract.address', '==', contract?.address),
              where('contract.itemId', '==', contract?.itemId),
              orderBy('date', 'desc'),
            );
      const { docs } = await getDocs(
        transactionsQuery.withConverter(
          ConverterFactory.buildDateConverter<Transaction<Date>>(),
        ),
      );
      setTransactions(
        docs.map((doc): Transaction<Date> => {
          return doc.data();
        }),
      );
      if (parent === 'item') {
        const fulfillmentsRef = collectionGroup(firestore, 'RentalFulfillment');
        const rentalsQuery = query(
          fulfillmentsRef,
          where('nft.address', '==', contract?.address),
          where('nft.identifier', '==', contract?.itemId),
        );
        const { docs } = await getDocs(
          rentalsQuery.withConverter(
            ConverterFactory.buildDateConverter<RentalFulfillment<Date>>(),
          ),
        );
        const rentalsConverted = docs.map((doc): Transaction<Date> => {
          const fulfillment = doc.data();
          const converter = new FulfillmentConverter(fulfillment);
          return converter.convertToTransaction();
        });
        setTransactions((prev) => {
          return [...prev, ...rentalsConverted].sort((a, b) => {
            return b.blockTimestamp.getTime() - a.blockTimestamp.getTime();
          });
        });
      }
    };
    handler();
  }, [contract?.address, contract?.itemId, parent, id]);

  return (
    <Box
      id="#history"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        background: ELEVATIONS[2],
        boxShadow: 1,
        borderRadius: '4px',
      }}
    >
      {transactions.length > 0 ? (
        <>
          {transactionsToDisplay.map((transaction, idx) => {
            if (idx === 0) {
              return (
                <Box key={transaction.id} ref={ref} sx={{ width: '100%' }}>
                  <TransactionTableRow transaction={transaction} />
                </Box>
              );
            }
            return (
              <TransactionTableRow
                transaction={transaction}
                key={transaction.id}
              />
            );
          })}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'flex-end',
              width: '100%',
              boxShadow: 'inset 0px 1px 0px rgba(255, 255, 255, 0.12)',
            }}
          >
            <PaginationOptions
              numItems={transactions.length}
              currentPage={currentPage}
              handleNextPage={handleNextPage}
              handlePrevPage={handlePreviousPage}
              rowsPerPage={rowsPerPage}
              handleRowsPerPage={handleRowsPerPage}
            />
          </Box>
        </>
      ) : (
        // : transactionsQuery.isLoading ? (
        //   <Skeleton height={350} width={'100%'} variant="rectangular" />
        // )
        <Typography sx={{ p: 4 }}>
          No transaction activity available.
        </Typography>
      )}
    </Box>
  );
}
